<template>
  <v-layout class="main-container" align-center column :style="{ backgroundColor: pageBackground}">
    <GoToHomepage class="homepage" :is-branded="isBranded" :branding="branding"/>
    <VcitaAlert
      v-if="shouldDisplayLinkInvalidMessage && showResetYourPassword"
      class="forgot-password-alert"
      type="warning"
      :body-text="$t('common.login.passwords.reset_link_no_longer_valid')"/>
    <v-layout class="forgot-password-container" align-center column>
        <template v-if="showResetYourPassword">
          <span class="forgot-password-title mt-8 mt-md-10">{{$t('common.login.passwords.reset_your_password_title')}}</span>
          <span class="subtitle my-3">{{$t('common.login.passwords.enter_email_to_receive_sub_title_instructions')}}</span>
          <v-form class="form" @submit.prevent>
            <v-text-field
              class="vTextField my-4"
              v-model="encodedInputEmail"
              type="email"
              :error-messages="emailError"
              :label="$t('common.login.email')"
              @keyup.enter="sendResetPasswordEmail"
              required
              outlined
            ></v-text-field>
            <VcButton class="send-button mt-3" :label="$t('common.login.passwords.send_reset_password_email')" @click="sendResetPasswordEmail" :loading="resetButtonLoading"></VcButton>
          </v-form>
          <a class="link" @click="backTologin">{{$t('common.login.passwords.login_with_different_account')}}</a>
        </template>
        <template v-else>
          <CheckYourMailboxImg v-if="!tooManyAttempts" class="mailbox-image"/>
          <span class="forgot-password-title mt-5">{{titleText}}</span>
          <template v-if="!tooManyAttempts">
            <span class="subtitle mb-8" v-html="this.$t('common.login.passwords.if_account_associated', {email:this.encodedInputEmail})"></span>
          </template>
          <span v-else class="subtitle mb-8">{{this.$t('common.login.passwords.too_many_attempts.sub_title')}}</span>
          <a class="link ml-2" @click="backTologin">{{$t('common.login.passwords.back_to_login')}}</a>
        </template>
      </v-layout>
  </v-layout>
</template>

<script>
import {authService} from '@/modules/auth/services/authService.js'
import {mapGetters} from "vuex";
import {oldDashboardUtil} from '@/modules/common/utils'
import GoToHomepage from '@/modules/auth/components/GoToHomepage'
import VcitaAlert from "@/modules/common/components/VcitaAlert/VcitaAlert";

export default {
  name: "ForgotPassword",
  components: {
    GoToHomepage,
    VcitaAlert,
    CheckYourMailboxImg: () => import('@/assets/images/check_your_mailbox.svg'),
  },
  data(){
    return{
      inputEmail: this.email,
      emailError: '',
      showResetYourPassword: true,
      tooManyAttempts: false,
      resetButtonLoading: false
    }
  },
  props: {
    shouldDisplayLinkInvalidMessage: {
      type: Boolean,
      default: false
    },
    email: {
      default: '',
      type: String
    }
  },
  computed:{
    ...mapGetters(['isMobile']),
    ...mapGetters("brandModule", ["branding","isBranded"]),
    isInputEmailValid(){
      return this.encodedInputEmail.match(/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)+$/)
    },
    titleText(){
      return this.tooManyAttempts ? this.$t('common.login.passwords.too_many_attempts.title') : this.$t('common.login.passwords.check_your_mailbox')

    },
    pageBackground(){
      if (this.isBranded) return this.branding.primary_color
    },
    encodedInputEmail: {
      get: function() {
        return this.inputEmail.replaceAll(' ','+')
      },
      set: function(email){
        return this.inputEmail = email
      }
    }
  },
  methods:{
    async sendResetPasswordEmail(){
      if(this.isInputEmailValid){
        this.emailError = ''
        this.resetButtonLoading = true
        let result = await authService.sendResetPasswordEmail({email:this.encodedInputEmail})
        this.showResetYourPassword = false
        if(result && result.data && result.data.email_status === 'too_many_attempts')
          this.tooManyAttempts = true
      }
      else {
        this.emailError = this.$t('common.login.passwords.errors.invalid_email_address')
      }
    },
    backTologin(){
      const link = oldDashboardUtil.getUrl('/login')
      window.open(link, '_self')
    }
  }
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/abstract/_variables.scss";
@import "@/assets/scss/abstract/_mixins.scss";

.main-container {
  padding-top: 30px;
  background-color: #FFFFFF;
  @include md-and-up{
    height: 100vh;
    background: rgba(245, 250, 253, 1);
  }

  .homepage{
    margin-top: 24px;
    margin-bottom: 30px;
    @include md-and-up{
      margin-bottom: 58px;
      margin-top: 50px;
    }
  }

  .regular_link {
    display: flex;
  }

  .forgot-password-alert {
    width: 100%;
    @include md-and-up{
      width: 611px;
    }

    &.dialog-alert{
      padding: 12px;
      padding-inline-end: 12px;

      &.v-alert {
        margin-bottom: 0px;
      }

      &.vcita-alert-warning{
        @include md-and-up{
          border-radius: 6px 6px 0px 0px;
          box-shadow:  0px 19px 0px 0px $white-color-1, 0px 0px 18px rgba(145, 152, 160, 0.25);
        }
      }
    }

    + .forgot-password-container {
      border-radius: 0px 0px 6px 6px;
    }
  }

  .forgot-password-container {
    width: 100%;
    @include md-and-up{
      width: 611px;
      height: 520px;
      max-height: 520px;
      box-shadow: 0px 4px 18px rgba(145, 152, 160, 0.25);
      border-radius: 6px;
    }
    margin: 0 auto;
    background: $white-color-1;

    .form {
      flex: 1 0 auto;
      width: 85%;
      @include md-and-up{
        width: 68%;
      }
    }

    .mailbox-image {
      width: 150px;
      min-height: 150px;
      max-height: 200px;
      @include md-and-up{
        margin-top:60px;
      }
    }

    .boldEmail{
      font-weight: 800;
    }

    .link {
      color: $blue-color-4;
      font-family: var(--primary-font-family);
      font-style: normal;
      font-weight: 500;
      font-size: 13px;
      line-height: 15px;
      height: 15px;
      text-align: center;
      margin-bottom: 30px;
      @include sm-and-down{
        margin-top: 20px;
      }
    }

    ::v-deep.vTextField.v-text-field {
      max-height: 50px;
      width: 100%;
      background: $white-color-1;
      box-sizing: border-box;
      border-radius: 6px;

      .v-input__slot fieldset{
        border: none;
      }

      &.v-input--is-focused label{
        margin-top: 8px;
      }

      & label.error--text{
        color:#616161 !important;
      }

      &.error--text .v-text-field__details{
        padding: 0px 0px 10px 0px !important;
        margin-bottom: 10px;
      }

      &.v-input--is-label-active, &.v-input--is-focused {
        label {
          font-family: var(--primary-font-family);
          font-style: normal;
          font-weight: normal;
          margin-top:8px;
          font-size: 14px;
          color: #616161 !important;
          line-height: 16px;
          transform: translateY(-18px) scale(0.85);
        }

        input {
          margin-top:14px;
          font-family: var(--primary-font-family);
          font-style: normal;
          font-weight: 500;
          font-size: 15px;
          line-height: 20px;
        }
      }

      &:not(.v-text-field--outlined) {
        .v-text-field__slot {
          transition: margin-bottom 0.1s;
        }

        &.v-input--is-label-active, &.v-input--is-focused {
          .v-text-field__slot {
            margin-bottom: 0;
          }

          .v-input__slot {
            padding-top: 20px;
          }
        }
      }

      .v-input__slot {
        height: 52px;
        border-radius: 6px;
        background-color: #fff !important;
        position: relative !important;
        border: 1px solid #E0E0E0;
        transition: border-color 0.1s !important;

        &:hover, &:focus-within {
          border-color: var(--v-secondary-base);
          box-shadow: 0 0 0 3px var(--v-secondary-lighten1);
          transition: box-shadow 0.1s !important;
        }

        &:before, &:after {
          display: none;
        }
      }

      .v-text-field__prefix {
        color: rgba(0, 0, 0, 0.54) !important;
      }

      &.error--text {
        .v-input__slot {
          border: 1px solid red;
          box-shadow: none;
        }
        margin-bottom: 20px;
      }

      &.v-input--is-disabled .v-input__slot{
        background-color: rgba(0, 0, 0, 0.03) !important;
      }
    }

    .send-button {
      width: 100%;
      height: auto;
      font-family: var(--primary-font-family);
      font-style: normal;
      font-weight: bold;
      font-size: 15px;
      line-height: 24px;
      letter-spacing: 0.003em;
      color: #FFFFFF;

      ::v-deep.v-btn__content {
        flex-flow: row wrap;
        width: 100%;
        white-space: break-spaces;
      }
    }

    .forgot-password-title {
      font-family: var(--primary-font-family);
      font-style: normal;
      font-weight: 600;
      font-size: 24px;
      line-height: 29px;
      text-align: center;
      max-width: 80%;
    }

    .subtitle {
      font-family: var(--primary-font-family);
      font-style: normal;
      font-weight: normal;
      font-size: 14px;
      line-height: 21px;
      text-align: center;
      color: #616161;
      margin-top: 8px;
      max-width: 80%;
      @include md-and-up{
        max-width: 60%;
      }
    }
  }
}

</style>
